import {
	DEFAULT_WORKSPACE_SCHEDULE_SETTINGS,
	type WorkspaceScheduleSettings,
} from "@elaso/shared/types/db";
import { useMemo } from "react";
import { useStore } from "zustand";
import { useRoleKeys } from "@/hooks/use-role-keys";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

export type UserScheduleSession = {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
	categoryId: string;
	channels: Array<{ channelId: string; channelName: string }>;
};

function sortSessionsByStartTime(sessions: UserScheduleSession[]) {
	sessions.sort(
		(a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
	);
}

function getFutureVisibleSessions<
	T extends { endTime: string; isVisible?: boolean },
>(sessions: Iterable<T>): T[] {
	const now = new Date();
	return Array.from(sessions).filter(
		(session) => new Date(session.endTime) >= now && session.isVisible !== false
	);
}

function buildAllSessionChannels(
	sessionIds: Set<string>,
	sessionChannelRoles: { sessionId: string; channelId: string }[],
	sessionChannelUsers: { sessionId: string; channelId: string }[]
) {
	const channelsBySession = new Map<string, Set<string>>();

	for (const assignment of sessionChannelUsers) {
		if (!sessionIds.has(assignment.sessionId)) continue;
		if (!channelsBySession.has(assignment.sessionId)) {
			channelsBySession.set(assignment.sessionId, new Set());
		}
		channelsBySession.get(assignment.sessionId)?.add(assignment.channelId);
	}

	for (const assignment of sessionChannelRoles) {
		if (!sessionIds.has(assignment.sessionId)) continue;
		if (!channelsBySession.has(assignment.sessionId)) {
			channelsBySession.set(assignment.sessionId, new Set());
		}
		channelsBySession.get(assignment.sessionId)?.add(assignment.channelId);
	}

	return channelsBySession;
}

function normalizeScheduleSettings(
	scheduleSettings: unknown
): WorkspaceScheduleSettings {
	if (!scheduleSettings || typeof scheduleSettings !== "object") {
		return {
			scheduleTitle: DEFAULT_WORKSPACE_SCHEDULE_SETTINGS.scheduleTitle,
			fullScheduleRoleKeys: [
				...DEFAULT_WORKSPACE_SCHEDULE_SETTINGS.fullScheduleRoleKeys,
			],
		};
	}

	const settings = scheduleSettings as {
		scheduleTitle?: unknown;
		fullScheduleRoleKeys?: unknown;
	};

	return {
		scheduleTitle:
			typeof settings.scheduleTitle === "string"
				? settings.scheduleTitle
				: DEFAULT_WORKSPACE_SCHEDULE_SETTINGS.scheduleTitle,
		fullScheduleRoleKeys: Array.isArray(settings.fullScheduleRoleKeys)
			? settings.fullScheduleRoleKeys.filter(
					(roleKey): roleKey is string => typeof roleKey === "string"
				)
			: [...DEFAULT_WORKSPACE_SCHEDULE_SETTINGS.fullScheduleRoleKeys],
	};
}

function toUserScheduleSessions(
	sessions: {
		id: string;
		name: string;
		startTime: string;
		endTime: string;
		categoryId: string;
	}[],
	channelsBySession: Map<string, Set<string>>,
	channels: Map<string, { name: string }>
): UserScheduleSession[] {
	const mapped = sessions.map((session) => {
		const channelIds = channelsBySession.get(session.id) ?? new Set<string>();
		return {
			id: session.id,
			name: session.name,
			startTime: session.startTime,
			endTime: session.endTime,
			categoryId: session.categoryId,
			channels: Array.from(channelIds).map((channelId) => ({
				channelId,
				channelName: channels.get(channelId)?.name ?? "Unknown",
			})),
		};
	});

	sortSessionsByStartTime(mapped);
	return mapped;
}

function buildUserAssignedSessionChannels({
	sessionIds,
	sessionChannelUsers,
	sessionChannelRoles,
	uid,
	userRoleKeys,
}: {
	sessionIds: Set<string>;
	sessionChannelUsers: {
		sessionId: string;
		channelId: string;
		userId: string;
	}[];
	sessionChannelRoles: {
		sessionId: string;
		channelId: string;
		roleKey: string;
	}[];
	uid: string;
	userRoleKeys: Set<string>;
}): Map<string, Set<string>> {
	const channelsBySession = new Map<string, Set<string>>();

	for (const assignment of sessionChannelUsers) {
		if (!sessionIds.has(assignment.sessionId) || assignment.userId !== uid) {
			continue;
		}

		if (!channelsBySession.has(assignment.sessionId)) {
			channelsBySession.set(assignment.sessionId, new Set());
		}

		channelsBySession.get(assignment.sessionId)?.add(assignment.channelId);
	}

	for (const assignment of sessionChannelRoles) {
		if (
			!(
				sessionIds.has(assignment.sessionId) &&
				userRoleKeys.has(assignment.roleKey)
			)
		) {
			continue;
		}

		if (!channelsBySession.has(assignment.sessionId)) {
			channelsBySession.set(assignment.sessionId, new Set());
		}

		channelsBySession.get(assignment.sessionId)?.add(assignment.channelId);
	}

	return channelsBySession;
}

export function useHasFullScheduleAccess(): boolean {
	const user = useStore(authStore, (state) => state.user);
	const users = useStore(coreStore, (state) => state.users);
	const workspaces = useStore(coreStore, (state) => state.workspaces);
	const currentWorkspaceId = useStore(
		uiStore,
		(state) => state.currentWorkspaceId
	);
	const { keysForRefs } = useRoleKeys(currentWorkspaceId);

	return useMemo(() => {
		const uid = user?.uid;
		if (!uid) return false;

		const currentWorkspace = workspaces.get(currentWorkspaceId || "");
		const workspaceWithScheduleSettings = currentWorkspace as
			| { scheduleSettings?: unknown }
			| undefined;
		const scheduleSettings = normalizeScheduleSettings(
			workspaceWithScheduleSettings?.scheduleSettings
		);
		if (scheduleSettings.fullScheduleRoleKeys.length === 0) return false;

		const currentUser = users.get(uid);
		// The caller's role keys, read off the roster rows the server sent rather
		// than spelled here (TECH-3336). A ref the roster cannot name contributes
		// no key, and could not have matched a stored key anyway.
		const userRoleKeys = new Set(keysForRefs(currentUser?.roles ?? []));

		return scheduleSettings.fullScheduleRoleKeys.some((roleKey) =>
			userRoleKeys.has(roleKey)
		);
	}, [user?.uid, users, workspaces, currentWorkspaceId, keysForRefs]);
}

export function useFullScheduleSessions(): UserScheduleSession[] {
	const sessions = useStore(coreStore, (state) => state.sessions);
	const sessionChannelRoles = useStore(
		coreStore,
		(state) => state.sessionChannelRoles
	);
	const sessionChannelUsers = useStore(
		coreStore,
		(state) => state.sessionChannelUsers
	);
	const channels = useStore(coreStore, (state) => state.channels);

	return useMemo(() => {
		const futureSessions = getFutureVisibleSessions(sessions.values());
		if (futureSessions.length === 0) return [];

		const sessionIds = new Set(futureSessions.map((session) => session.id));
		const channelsBySession = buildAllSessionChannels(
			sessionIds,
			sessionChannelRoles,
			sessionChannelUsers
		);

		return toUserScheduleSessions(futureSessions, channelsBySession, channels);
	}, [sessions, sessionChannelRoles, sessionChannelUsers, channels]);
}

export function useHasSchedule(): boolean {
	const hasFullScheduleAccess = useHasFullScheduleAccess();
	const userSessions = useUserScheduleSessions();

	return hasFullScheduleAccess || userSessions.length > 0;
}

export function useUserScheduleSessions(): UserScheduleSession[] {
	const user = useStore(authStore, (state) => state.user);
	const sessions = useStore(coreStore, (state) => state.sessions);
	const sessionChannelRoles = useStore(
		coreStore,
		(state) => state.sessionChannelRoles
	);
	const sessionChannelUsers = useStore(
		coreStore,
		(state) => state.sessionChannelUsers
	);
	const users = useStore(coreStore, (state) => state.users);
	const channels = useStore(coreStore, (state) => state.channels);
	const currentWorkspaceId = useStore(
		uiStore,
		(state) => state.currentWorkspaceId
	);
	const { keysForRefs } = useRoleKeys(currentWorkspaceId);

	const uid = user?.uid;

	return useMemo(() => {
		if (!uid) return [];

		const currentUser = users.get(uid);
		// See `useHasFullScheduleAccess` above: keys come from the roster.
		const userRoleKeys = new Set(keysForRefs(currentUser?.roles ?? []));
		const futureSessions = getFutureVisibleSessions(sessions.values());

		if (futureSessions.length === 0) return [];

		const sessionIds = new Set(futureSessions.map((s) => s.id));
		const channelsBySession = buildUserAssignedSessionChannels({
			sessionIds,
			sessionChannelUsers,
			sessionChannelRoles,
			uid,
			userRoleKeys,
		});

		const matchedSessions = futureSessions.filter((s) =>
			channelsBySession.has(s.id)
		);

		if (matchedSessions.length === 0) return [];

		return toUserScheduleSessions(matchedSessions, channelsBySession, channels);
	}, [
		uid,
		sessions,
		sessionChannelRoles,
		sessionChannelUsers,
		users,
		channels,
		keysForRefs,
	]);
}
